import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { MOBILE_ROLES } from "@/lib/workflow";
import InstallPrompt from "@/components/InstallPrompt";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import MobileMyProjects from "@/pages/MobileMyProjects";
import MobileProjectView from "@/pages/MobileProjectView";
import MobilePlaceholder from "@/pages/MobilePlaceholder";
import UsersAdmin from "@/pages/UsersAdmin";
import Settings from "@/pages/Settings";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Quotes from "@/pages/Quotes";
import QuoteBuilder from "@/pages/QuoteBuilder";
import QuoteDetail from "@/pages/QuoteDetail";
import QuoteSettings from "@/pages/QuoteSettings";
import EnquiryPublic from "@/pages/EnquiryPublic";
import Enquiries from "@/pages/Enquiries";
import EnquiryNew from "@/pages/EnquiryNew";
import EnquiryDetail from "@/pages/EnquiryDetail";
import DesignWorkItem from "@/pages/DesignWorkItem";
import ItemIdSettings from "@/pages/ItemIdSettings";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import CostingMargin from "@/pages/CostingMargin";
import PurchaseBoard from "@/pages/PurchaseBoard";
import SalesReceivables from "@/pages/SalesReceivables";
import ProductionDesignWork from "@/pages/ProductionDesignWork";
import DesignWork from "@/pages/DesignWork";
import ReviewPublic from "@/pages/ReviewPublic";
import Store from "@/pages/Store";

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <div className="flex items-center justify-center h-screen text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (MOBILE_ROLES.includes(user.role)) return <Navigate to="/m" replace />;
  // Store role lands directly on the Store page — the standard dashboard
  // (enquiries / quotes / project timelines) is intentionally hidden.
  if (user.role === "store") return <Navigate to="/store" replace />;
  // Desktop roles: unified dashboard is the new home.
  return <Navigate to="/dashboard" replace />;
}

// Mobile route guard: requires login but mobile roles are allowed without redirect.
function ProtectedRouteMobile({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="flex items-center justify-center h-screen text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Public enquiry page (no auth) */}
            <Route path="/enquiry" element={<EnquiryPublic />} />
            {/* Public review page (no auth) — customer post-installation */}
            <Route path="/review/:token" element={<ReviewPublic />} />

            {/* Mobile home for shop-floor roles — production / installation.
                Lists projects in the user's owning stage(s). */}
            <Route path="/m" element={
              <ProtectedRouteMobile><MobileMyProjects /></ProtectedRouteMobile>
            } />
            <Route path="/m/project/:id" element={
              <ProtectedRouteMobile><MobileProjectView /></ProtectedRouteMobile>
            } />

            {/* Desktop routes */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/dashboard" element={
              <ProtectedRoute roles={["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "design", "production_design", "dealer", "production", "installation"]}>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/users" element={<ProtectedRoute roles={["super_admin"]}><UsersAdmin /></ProtectedRoute>} />
            <Route path="/customers" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "super_admin"]}><Customers /></ProtectedRoute>
            } />
            <Route path="/customers/:id" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "super_admin"]}><CustomerDetail /></ProtectedRoute>
            } />
            <Route path="/settings" element={<ProtectedRoute roles={["super_admin", "accounts", "dealer"]}><Settings /></ProtectedRoute>} />
            <Route path="/quote-settings" element={<ProtectedRoute roles={["super_admin"]}><QuoteSettings /></ProtectedRoute>} />
            <Route path="/quotes" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "accounts", "super_admin", "dealer"]}><Quotes /></ProtectedRoute>
            } />
            <Route path="/quotes/new" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "accounts", "super_admin", "dealer"]}><QuoteBuilder /></ProtectedRoute>
            } />
            <Route path="/quotes/:id" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "accounts", "super_admin", "dealer"]}><QuoteDetail /></ProtectedRoute>
            } />
            <Route path="/quotes/:id/edit" element={
              <ProtectedRoute roles={["sales", "sales_coordination", "production_coordination", "accounts", "super_admin", "dealer"]}><QuoteBuilder /></ProtectedRoute>
            } />

            {/* Enquiries — internal pipeline */}
            <Route path="/enquiries" element={
              <ProtectedRoute roles={["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"]}><Enquiries /></ProtectedRoute>
            } />
            <Route path="/enquiries/new" element={
              <ProtectedRoute roles={["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"]}><EnquiryNew /></ProtectedRoute>
            } />
            <Route path="/enquiries/:id" element={
              <ProtectedRoute roles={["super_admin", "sales", "sales_coordination", "production_coordination", "dealer"]}><EnquiryDetail /></ProtectedRoute>
            } />
            <Route path="/design/:id" element={
              <ProtectedRoute roles={["super_admin", "design"]}><DesignWorkItem /></ProtectedRoute>
            } />

            {/* Projects — 9-stage pipeline */}
            <Route path="/projects" element={
              <ProtectedRoute roles={["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "production_design", "dealer"]}><Projects /></ProtectedRoute>
            } />
            <Route path="/projects/:id" element={
              <ProtectedRoute redirectMobile={false} roles={["super_admin", "sales", "sales_coordination", "accounts", "production_coordination", "admin", "design", "production_design", "dealer", "production", "installation"]}><ProjectDetail /></ProtectedRoute>
            } />

            <Route path="/settings/item-ids" element={
              <ProtectedRoute roles={["super_admin", "production_design"]}><ItemIdSettings /></ProtectedRoute>
            } />

            <Route path="/costing" element={
              <ProtectedRoute roles={["super_admin", "accounts"]} permission="access_costing"><CostingMargin /></ProtectedRoute>
            } />
            <Route path="/costing/purchase-board" element={
              <ProtectedRoute roles={["super_admin", "accounts", "admin", "production_coordination"]} permission="access_costing"><PurchaseBoard /></ProtectedRoute>
            } />
            <Route path="/sales" element={
              <ProtectedRoute roles={["super_admin", "accounts", "admin", "sales", "sales_coordination"]} permission="access_sales_receivables"><SalesReceivables /></ProtectedRoute>
            } />
            <Route path="/production-design/work" element={
              <ProtectedRoute roles={["super_admin", "production_design"]}><ProductionDesignWork /></ProtectedRoute>
            } />
            <Route path="/design/work" element={
              <ProtectedRoute roles={["super_admin", "design"]}><DesignWork /></ProtectedRoute>
            } />

            <Route path="/store" element={
              <ProtectedRoute roles={["super_admin", "store", "accounts", "admin"]}><Store /></ProtectedRoute>
            } />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <InstallPrompt />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
